import React from "react";
import { useParams, Link } from "react-router-dom";
import { teamMembers, recentSold, giannaReviews, omerReviews, sameerReviews, ranyaReviews } from "../mock";
import ContactForm from "../components/heider/ContactForm";
import Footer from "../components/heider/Footer";
import PropertyCard from "../components/heider/PropertyCard";
import SwipeRow from "../components/heider/SwipeRow";
import useReveal from "../hooks/useReveal";
import { Star, Phone, Mail, ArrowLeft } from "lucide-react";

const reviewsBySlug = {
  "gianna-manzella": giannaReviews,
  "omer-reshid": omerReviews,
  "sameer-yousaf": sameerReviews,
  "ranya-belkhir": ranyaReviews,
};

const ReviewsSection = ({ slug, rating, reviews }) => {
  const list = reviewsBySlug[slug];
  const [filter, setFilter] = React.useState("all");
  if (!list || !list.length) return null;
  const count = (k) => list.filter((r) => r.type === k).length;
  const tabs = [
    { k: "all", label: "All reviews", n: list.length },
    { k: "buy", label: "Helped me buy", n: count("buy") },
    { k: "sell", label: "Helped me sell", n: count("sell") },
    { k: "rent", label: "Renting / property mgmt", n: count("rent") },
  ].filter((t) => t.n > 0);
  const shown = filter === "all" ? list : list.filter((r) => r.type === filter);
  const attrs = ["Local knowledge", "Process expertise", "Responsiveness", "Negotiation skills"];
  return (
    <section className="bg-[#f3f1ec] py-24 md:py-28">
      <div className="max-w-[1000px] mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10 reveal">
          <div>
            <p className="text-[11px] tracking-luxe uppercase text-[#b4936a] mb-3">Client Reviews</p>
            <h2 className="font-serif-display text-4xl md:text-5xl font-light text-[#1a1a1a]">What Clients Say</h2>
          </div>
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1 text-[#b4936a]"><Star size={22} fill="#b4936a" /></span>
            <span className="font-serif-display text-4xl text-[#1a1a1a]">{rating || "5.0"}</span>
            <span className="text-neutral-500 text-[13px]">{reviews} reviews</span>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-10">
          {tabs.map((t) => (
            <button
              key={t.k}
              onClick={() => setFilter(t.k)}
              className={`text-[11px] tracking-luxe-sm uppercase px-4 py-2.5 border transition-colors ${filter === t.k ? "bg-[#0d0d0d] text-white border-[#0d0d0d]" : "border-neutral-300 text-neutral-600 hover:border-[#b4936a] hover:text-[#b4936a]"}`}
            >
              {t.label} ({t.n})
            </button>
          ))}
        </div>

        <div className="space-y-6">
          {shown.map((r, i) => (
            <div key={i} className="bg-white p-7 md:p-8 border border-black/[0.06] shadow-[0_20px_50px_rgba(0,0,0,0.04)] reveal">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-1 text-[#b4936a]">
                  {[...Array(5)].map((_, s) => <Star key={s} size={15} fill="#b4936a" />)}
                </div>
                <span className="text-[12px] text-neutral-400">{r.date}</span>
              </div>
              <p className="font-serif-display text-xl text-[#1a1a1a] mb-1">{r.name}</p>
              <p className="text-[11px] tracking-luxe-sm uppercase text-neutral-500 mb-4">{r.context}</p>
              <p className="text-neutral-600 text-[15px] leading-relaxed mb-5">{r.text}</p>
              <div className="flex flex-wrap gap-2">
                {attrs.map((a) => (
                  <span key={a} className="text-[10px] tracking-luxe-sm uppercase text-neutral-500 bg-neutral-100 px-3 py-1.5">{a}</span>
                ))}
              </div>
            </div>
          ))}
          {shown.length === 0 && <p className="text-neutral-500 text-center py-8">No reviews in this category yet.</p>}
        </div>
      </div>
    </section>
  );
};

const AgentProfile = () => {
  const { slug } = useParams();
  useReveal(slug);
  const m = teamMembers.find((x) => x.slug === slug);

  if (!m) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#f3f1ec] text-center px-6">
        <h1 className="font-serif-display text-4xl mb-4">Advisor not found</h1>
        <Link to="/team" className="text-[#b4936a] tracking-luxe-sm uppercase text-[12px]">Back to Team</Link>
      </div>
    );
  }

  const stats = [
    m.sales != null && { v: m.sales, l: "Sales Last 12 Months" },
    m.total != null && { v: m.total, l: "Total Sales" },
    m.priceRange && { v: m.priceRange, l: "Price Range" },
    m.avg && { v: m.avg, l: "Average Price" },
  ].filter(Boolean);

  return (
    <div className="page-in">
      {/* Header band */}
      <section className="bg-[#0d0d0d] text-white pt-32 md:pt-40 pb-16 md:pb-20">
        <div className="max-w-[1300px] mx-auto px-6 md:px-10">
          <Link to="/team" className="inline-flex items-center gap-2 text-[11px] tracking-luxe uppercase text-white/60 hover:text-[#b4936a] transition-colors mb-10">
            <ArrowLeft size={14} /> All Advisors
          </Link>
          <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-10 lg:gap-16 items-center">
            <div className="relative w-56 h-56 lg:w-[300px] lg:h-[300px] rounded-full overflow-hidden ring-2 ring-[#b4936a]/40 mx-auto lg:mx-0">
              <img src={m.image} alt={m.name} className="w-full h-full object-cover object-top" />
            </div>
            <div className="text-center lg:text-left">
              <h1 className="font-serif-display text-5xl md:text-7xl font-light mb-3">{m.name}</h1>
              <p className="text-[11px] tracking-luxe uppercase text-[#b4936a] mb-4">{m.role} &middot; Capital Corner Group</p>
              {m.rating && (
                <div className="flex items-center justify-center lg:justify-start gap-2 mb-8">
                  <Star size={16} fill="#b4936a" className="text-[#b4936a]" />
                  <span className="text-lg">{m.rating}</span>
                  <span className="text-white/50 text-[13px]">{m.reviews} reviews</span>
                </div>
              )}
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4">
                {m.phone && <a href={`tel:${m.phone}`} className="inline-flex items-center gap-2 bg-[#b4936a] text-white text-[11px] tracking-luxe uppercase px-6 py-3 hover:bg-[#a07f57] transition-colors"><Phone size={14} /> {m.phone}</a>}
                {m.email && <a href={`mailto:${m.email}`} className="inline-flex items-center gap-2 border border-white/40 text-white text-[11px] tracking-luxe uppercase px-6 py-3 hover:bg-white hover:text-black transition-colors"><Mail size={14} /> Email</a>}
              </div>
            </div>
          </div>

          {stats.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-16 border-t border-white/15 pt-12">
              {stats.map((s, i) => (
                <div key={i} className="text-center">
                  <div className="font-serif-display text-3xl md:text-5xl mb-2">{s.v}</div>
                  <div className="text-white/50 text-[10px] tracking-luxe uppercase">{s.l}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* About + specialties */}
      <section className="bg-[#f3f1ec] py-24 md:py-28">
        <div className="max-w-[1000px] mx-auto px-6 md:px-10 grid md:grid-cols-[1fr_260px] gap-14">
          <div className="reveal">
            <p className="text-[11px] tracking-luxe uppercase text-[#b4936a] mb-5">Get to know {m.name.split(" ")[0]}</p>
            <p className="font-serif-display text-2xl md:text-[1.7rem] leading-relaxed text-[#2a2a2a] font-light">{m.bio}</p>
          </div>
          <div className="reveal-left">
            <p className="text-[11px] tracking-luxe uppercase text-[#b4936a] mb-5">Specialties</p>
            <ul className="space-y-3">
              {m.specialties.map((s) => (
                <li key={s} className="flex items-center gap-3 text-neutral-700 text-[15px]">
                  <span className="w-1.5 h-1.5 bg-[#b4936a] rounded-full" /> {s}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Recent sales */}
      {!m.role.includes("Loan") && (
      <section className="bg-white py-24 md:py-28">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10">
          <div className="mb-12 reveal">
            <p className="text-[11px] tracking-luxe uppercase text-[#b4936a] mb-3">Recent Sales</p>
            <h2 className="font-serif-display text-4xl md:text-5xl font-light text-[#1a1a1a]">{m.name.split(" ")[0]}&rsquo;s Track Record</h2>
          </div>
          <div className="reveal">
            <SwipeRow testid="agent-recent-sales-swipe">
              {recentSold.slice(0, 6).map((item, i) => (
                <PropertyCard key={i} item={item} />
              ))}
            </SwipeRow>
          </div>
        </div>
      </section>
      )}

      <ReviewsSection slug={slug} rating={m.rating} reviews={m.reviews} />

      <ContactForm />
      <Footer />
    </div>
  );
};

export default AgentProfile;
